import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Facepile,
  Icon,
  IDropdownOption,
  IFacepilePersona,
  Persona,
  PersonaSize,
  DocumentCardActivity,
  IDocumentCardActivityPerson,
  Button,
  fontFace,
  Checkbox,
} from '@fluentui/react';
import { GRID_MODE } from '../../../common/list-utils';
import { DateCustom } from '../../../lib/DateCustom';
import { DetailsListView, EntityGridListProps } from '../../Common/DetailsList/DetailListView';
import i18next, { t } from 'i18next';
import './collaboratorCreate.css';
import CollaboratorCreate from './collaboratorCreate';
import { exportArrayToExcel } from '../../../common/exportArrayToExcel';
import { getCorporativoInfo, removeQueryParams } from '../../../lib/Util';
import axios from 'axios';
import { useRepresentadoRRHHConfig } from '../../../shared/hooks/useRepresentadoRRHHConfig';

const keysToExport = [
  {
    key: 'col_iniciales',
    header: t('Iniciales'),
  },
  {
    key: 'col_id_sap',
    header: t('Codigo.SAP'),
  },
  {
    key: 'col_nombre',
    header: t('Nombre'),
  },
  {
    key: 'col_apellido',
    header: t('Apellido'),
  },
  {
    key: 'cargo_nombre',
    header: t('Cargo'),
  },
  {
    key: 'col_email',
    header: t('Mail'),
  },
  {
    key: 'col_fecha_alta',
    header: t('Fecha de ingreso'),
  },
  {
    key: 'col_activo',
    header: t('Estado'),
    formatter: (col_activo) => (col_activo == 1 ? 'Activo ' : 'Inactivo'),
  },
  {
    key: 'col_fecha_baja',
    header: t('Fecha baja'),
  },
  {
    key: 'cnih_porcentaje_excento',
    header: t('Proyecto fijo'),
    formatter: (cnih_porcentaje_excento) => (cnih_porcentaje_excento !== null ? 'si' : 'no'),
  },
  {
    key: 'cnih_porcentaje_excento',
    header: t('PF-% participación'),
    formatter: (cnih_porcentaje_excento) => (cnih_porcentaje_excento ? cnih_porcentaje_excento.split('.')[0] + '%' : null),
  },
  {
    key: 'cnih_fecha_alta',
    header: t('PF-ingreso'),
  },
  {
    key: 'cnih_fecha_baja',
    header: t('PF-fin'),
  },
  {
    key: 'us_perid',
    header: t('Tipo de usuario'),
    formatter: (us_perid) =>
      us_perid === '00000005' ? 'User' : us_perid === '00000010' ? 'Directivo' : us_perid === '00000006' ? 'Power User' : 'Admin User',
  },
  {
    key: 'col_representado_rrhh',
    header: t('Representado por RRHH'),
    formatter: (col_representado_rrhh) => (col_representado_rrhh === '1' ? 'Si' : 'No'),
  },
];

export const CollaboratorGridList = (props: EntityGridListProps) => {
  const [viewCreate, setViewCreate] = useState<React.SetStateAction<number>>(0);
  const [toastExit, setToastExit] = useState<boolean>(false);
  const [listLevels, setListLeves] = useState<any>([]);
  const [items, setItems] = React.useState<any[]>([]);
  const [uriParams, setUriParams] = useState('');
  const [representadoRRHHValues, setRepresentadoRRHHValues] = useState<Record<string, string>>({});
  const [refresh, setRefresh] = useState(false)

  const configRepresentadoRRHH = useRepresentadoRRHHConfig(props.user);
  const canEditRepresentadoRRHH = configRepresentadoRRHH?.cltconf_action?.toUpperCase() === 'W';

  const handleRepresentadoRRHHChange = useCallback(
    async (item: any, checked: boolean) => {
      try {
        props.changeBlockUI(true);

        const value = checked ? '1' : '0';

        await props.system?.getCollaboratorSystem().updateRepresentadoRRHH(item.col_id, Number(value));

        setRepresentadoRRHHValues((prev) => ({
          ...prev,
          [item.col_id]: value,
        }));
        setRefresh((prev) => !prev)
      } catch (error) {
        console.error(error);

        props.courier.messageError(t('Error al actualizar Representado por RRHH'));
      } finally {
        props.changeBlockUI(false);
      }
    },
    [props],
  );

  const formatEquipo = (item: any) => {
    let equipo = (item.col_nombre + ' ' + item.col_apellido).split(' | ');

    var getInitials = function () {
      let initials = item.col_iniciales;
      return initials;
    };
    let equipoPersonas: IFacepilePersona[] = equipo.map((persona: string) => {
      return {
        personaName: persona,
        imageInitials: getInitials(),
      };
    });

    return (
      <Facepile
        personas={equipoPersonas}
        personaSize={10}
        maxDisplayablePersonas={6}
        overflowButtonProps={{ ariaLabel: 'Ver más' }}
        overflowButtonType={1}
        onRenderPersona={(props) => <Persona {...props} size={PersonaSize.size24} onRenderPrimaryText={() => <></>}></Persona>}
      />
    );
  };

  useEffect(() => {
    getLevelsNodes();
  }, []);

  const getLevelsNodes = async () => {
    const respuesta = await props.system?.getOrganizationChartSystem().getLevels();
    const formattedLevels = respuesta
      ? respuesta.map((item: any) => ({
          fieldName: item.nameTec,
          name: t(item.name),
        }))
      : [];
    setListLeves([...formattedLevels]);
  };

  const columns = useMemo(() => {
    return [
      {
        fieldName: 'col_iniciales',
        name: '',
        _onRender: (item: any) => formatEquipo(item),
        onRenderPersona: () => <></>,
        maxWidth: 40,
      },
      {
        fieldName: 'col_id_sap',
        name: t('Codigo.SAP'),
      },
      {
        fieldName: 'col_nombre',
        name: t('Nombre'),
      },
      {
        fieldName: 'col_apellido',
        name: t('Apellido'),
      },
      ...listLevels,
      {
        fieldName: 'cargo_nombre',
        name: t('Cargo'),
      },
      {
        fieldName: 'col_email',
        name: t('Mail'),
      },
      {
        fieldName: 'col_fecha_alta',
        name: t('Fecha de ingreso'),
      },
      {
        fieldName: 'col_activo',
        name: t('Estado'),
        _onRender: (item: any) => (item.col_activo == 1 ? 'Activo ' : 'Inactivo'),
      },
      {
        fieldName: 'col_fecha_baja',
        name: t('Fecha baja'),
      },
      {
        fieldName: 'cnih_porcentaje_excento',
        name: t('Proyecto fijo'),
        _onRender: (item: any) => (item.cnih_porcentaje_excento !== null ? 'si' : 'no'),
      },
      {
        fieldName: 'cnih_porcentaje_excento',
        name: t('PF-% participación'),
        _onRender: (item: any) => (item.cnih_porcentaje_excento ? item.cnih_porcentaje_excento.split('.')[0] + '%' : null),
      },
      {
        fieldName: 'cnih_fecha_alta',
        name: t('PF-ingreso'),
      },
      {
        fieldName: 'cnih_fecha_baja',
        name: t('PF-fin'),
      },
      {
        fieldName: 'us_perid',
        name: t('Tipo de usuario'),
        _onRender: (item: any) =>
          item.us_perid === '00000005'
            ? 'User'
            : item.us_perid === '00000010'
              ? 'Directivo'
              : item.us_perid === '00000006'
                ? 'Power User'
                : 'Admin User',
      },
      ...(configRepresentadoRRHH
        ? [
            {
              fieldName: 'col_representado_rrhh',
              name: t('Registro asistido'),
              _onRender: (item: any) => {
                const value = representadoRRHHValues[item.col_id] ?? item.col_representado_rrhh;

                return (
                  <div onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={value === '1'}
                      disabled={!canEditRepresentadoRRHH}
                      onChange={(_ev, checked) => handleRepresentadoRRHHChange(item, !!checked)}
                    />
                  </div>
                );
              },
            },
          ]
        : []),
    ];
  }, [listLevels, configRepresentadoRRHH, representadoRRHHValues, canEditRepresentadoRRHH, handleRepresentadoRRHHChange]);

  const exportExcel = async () => {
    try {
      props.changeBlockUI(true);
      props.courier.messageError('');
      console.log({ uriParams });

      // Se replical fecth del datagrid pero sin paginacion solo filtros del usuario
      const cleanUrl = removeQueryParams(uriParams, ['skip', 'limit']);
      // Obtener el TimeZone del usuario
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const {
        data: { data: colaboradores },
      } = await axios.get(cleanUrl, {
        withCredentials: true,
        headers: {
          'Time-Zone': timeZone,
        },
      });

      const corporativo = getCorporativoInfo();
      await exportArrayToExcel({
        fileName: 'Colaboradores',
        title: 'Reporte de colaboradores',
        data: colaboradores,
        columns: keysToExport,
        headerBackgroundColor: 'FF203864',
        headerFontColor: 'FFFFFFFF',
        extraInfoRows: [
          {
            label: 'Corporativo',
            value: corporativo?.empp_nombre || 'Principal',
          },
          {
            label: 'Generado',
            value: new Date().toLocaleString(),
          },
        ],
      });
      props.courier.messageSuccess(t('Se ha guardado exitosamente'));
    } catch (error) {
      console.log(error);
      props.courier.messageError(t('No hay datos disponibles'));
    } finally {
      props.changeBlockUI(false);
    }
  };

  return (
    <>
      {viewCreate === 0 ? (
        <div>
          {toastExit && (
            <div className="toast_container_collaborator">
              <div className="toast_message_collaborator">
                <Icon iconName="Completed" className="icon_toast_collaborator" />
                Se agregó al colaborador exitosamente.
              </div>
              <Icon iconName="Clear" onClick={() => setToastExit(false)} />
            </div>
          )}
          <div className="collaborator_create_hader_buton">
            <Button onClick={() => setViewCreate(1)} className="collaborator_create_bt">
              <Icon iconName="add" className="collaborator_create_icon" /> <p> {t('Nuevo')}</p>
            </Button>
            <Button onClick={exportExcel} className="collaborator_create_bt" disabled={items.length === 0}>
              <Icon iconName="ExcelLogo" className="collaborator_create_icon" /> <p> {t('Exportar')}</p>
            </Button>
          </div>
          {listLevels.length > 0 && (
            <DetailsListView
              refresh={refresh}
              keyList={'colaboradores'}
              onClickCardMode={() => props.onClickCardMode()}
              onClickGridMode={() => props.onClickGridMode()}
              mode={props.mode}
              title={t('Colaboradores')}
              onItemClick={props.onItemClick}
              cols={columns}
              isGridList={true}
              listFilters={[]}
              quickFilters={props.quickFilters}
              baseUrl={props.baseUrl}
              changeBlockUI={props.changeBlockUI}
              courier={props.courier}
              setItems={setItems}
              getUriParams={setUriParams}
            ></DetailsListView>
          )}
        </div>
      ) : (
        <CollaboratorCreate
          setviewcreate={setViewCreate}
          setToastExit={setToastExit}
          system={props.system}
          user={props.user}
          changeBlockUI={props.changeBlockUI}
          courier={props.courier}
        />
      )}
    </>
  );
};
